import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, AlertCircle, Database, HardDrive } from "lucide-react";

type HealthCheckData = {
  timestamp: number | Date;
  status: "ok" | "error" | "warning" | "healthy" | "degraded" | "unhealthy";
  version: string;
  nodeEnv: string;
  checks: {
    database: {
      status: "ok" | "error" | "warning" | "healthy" | "degraded" | "unhealthy";
      message: string;
      latency?: number;
    };
    storage: {
      status: "ok" | "error" | "warning" | "healthy" | "degraded" | "unhealthy";
      message: string;
      latency?: number;
    };
  };
};

interface SystemStatusCardsProps {
  healthData: HealthCheckData | null | undefined;
  isLoading?: boolean;
}

export default function SystemStatusCards({ healthData, isLoading }: SystemStatusCardsProps) {
  const getStatusBadge = (status: "ok" | "error" | "warning" | "healthy" | "degraded" | "unhealthy") => {
    const variants: Record<string, { variant: "default" | "destructive" | "secondary"; icon: any }> = {
      ok: { variant: "default", icon: CheckCircle2 },
      healthy: { variant: "default", icon: CheckCircle2 },
      warning: { variant: "secondary", icon: AlertCircle },
      degraded: { variant: "secondary", icon: AlertCircle },
      error: { variant: "destructive", icon: XCircle },
      unhealthy: { variant: "destructive", icon: XCircle },
    };

    const config = variants[status] || variants.warning;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {status.toUpperCase()}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Loading system status...</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Loading database status...</CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!healthData) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-red-600">Failed to load system status</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-red-600">Failed to load database status</CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      {/* Overall Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Overall Status</CardTitle>
              <CardDescription className="text-xs">
                Last checked: {new Date(healthData.timestamp).toLocaleString()}
              </CardDescription>
            </div>
            {getStatusBadge(healthData.status)}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">App Version</p>
              <p className="text-lg font-semibold">{healthData.version}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Environment</p>
              <p className="text-sm font-medium">{healthData.nodeEnv}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Database Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              <CardTitle>Database Connection</CardTitle>
            </div>
            {getStatusBadge(healthData.checks.database.status)}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm mb-2">{healthData.checks.database.message}</p>
          {healthData.checks.database.latency && (
            <p className="text-xs text-muted-foreground">
              Latency: {healthData.checks.database.latency}ms
            </p>
          )}
        </CardContent>
      </Card>

      {/* S3 Storage Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HardDrive className="h-5 w-5" />
              <CardTitle>S3 Storage</CardTitle>
            </div>
            {getStatusBadge(healthData.checks.storage.status)}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm mb-2">{healthData.checks.storage.message}</p>
          {healthData.checks.storage.latency && (
            <p className="text-xs text-muted-foreground">
              Latency: {healthData.checks.storage.latency}ms
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
